/** Initial booking lifecycle state on POST `/bookings/create` (API: Pending). Not shown in the UI. */
export const DEFAULT_BOOKING_CREATE_STATUS_ID = 1;

/** API requires an integer; use when the asset has no people-count field. */
export const DEFAULT_BOOKING_AMOUNT_OF_PEOPLE = 1;

export type BookingCreateAddonWire = {
  addon_id: number;
  quantity: number;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceAddonId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function coerceAmountOfPeople(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) {
    return Math.floor(raw);
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n) && n >= 1) {
      return Math.floor(n);
    }
  }
  return DEFAULT_BOOKING_AMOUNT_OF_PEOPLE;
}

function coerceAddonQuantity(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) {
    return Math.floor(raw);
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n) && n >= 1) {
      return Math.floor(n);
    }
  }
  return 1;
}

/** Map form / draft addon rows to POST `/bookings/create` wire shape. */
export function normalizeBookingCreateAddons(addons: unknown): BookingCreateAddonWire[] {
  if (!Array.isArray(addons)) {
    return [];
  }
  const byId = new Map<number, number>();
  for (const item of addons) {
    if (!isPlainObject(item)) {
      continue;
    }
    const addon_id = coerceAddonId(item.addon_id);
    if (addon_id === null) {
      continue;
    }
    byId.set(addon_id, coerceAddonQuantity(item.quantity));
  }
  return Array.from(byId.entries()).map(([addon_id, quantity]) => ({ addon_id, quantity }));
}

/**
 * Strip UI-only fields and normalize addons before POST `/bookings/create`.
 * The API rejects `pricing_type_id` on create; each addon needs integer `quantity` ≥ 1.
 */
export function normalizeBookingCreatePayload(body: Record<string, unknown>): Record<string, unknown> {
  const out = { ...body };
  delete out.pricing_type_id;
  delete out.booking_date;
  delete out.status_id;
  out.amount_of_people = coerceAmountOfPeople(out.amount_of_people);
  out.status_id = DEFAULT_BOOKING_CREATE_STATUS_ID;
  if ('addons' in out) {
    out.addons = normalizeBookingCreateAddons(out.addons);
  }
  // Keep vehicle_id (UUID string) for parking bookings when present.
  if (typeof out.vehicle_id === 'string') {
    const id = out.vehicle_id.trim();
    if (id) {
      out.vehicle_id = id;
    } else {
      delete out.vehicle_id;
    }
  }
  return out;
}

/**
 * POST `/bookings/create` rejects add-on lines on many API builds; create first, then PATCH add-ons.
 */
export function bookingCreateBodyWithoutAddons(body: Record<string, unknown>): Record<string, unknown> {
  return { ...body, addons: [] };
}
